import { type FC } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import Radio from "~/common/components/Radio";
import classes from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/v2/RequestFeedbackPopupForm.module.less";
import type { MultilevelSelectItemItem } from "~/common/components/Select/MultilevelSelect";
import { Accordion, AccordionDetails, AccordionSummary, TextField } from "~/common/mui";

interface Props {
  register: UseFormReturn["register"];
  control: UseFormReturn["control"];
  dropdownOptions: MultilevelSelectItemItem[];
}

const RequestFeedbackPopupForm: FC<Props> = (props: Props) => {
  const { t } = useTranslation();

  const CategorySelection = ({
    currentValue,
    optionList,
  }: {
    currentValue?: string;
    optionList?: MultilevelSelectItemItem[];
  }) => {
    if (!currentValue || !optionList?.some((child) => child.value === currentValue)) {
      return null;
    }
    return (
      <div className={classes.categorySelect}>
        <Icon type={IconType.Check} />
        <span>{optionList?.find((option) => option.value === currentValue)?.label}</span>
      </div>
    );
  };

  return (
    <>
      <p className={classes.subjectHeader}>{t("serviceCases.create.subject") + "*"}</p>
      <Controller
        // titles can't be selected, so don't put them as default value, put the first selectable option
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            {props.dropdownOptions.map((option) => (
              <Accordion>
                <AccordionSummary classes={{ content: classes.summaryContent }}>
                  <span>{option.label}</span>
                  <CategorySelection optionList={option.children} currentValue={value} />
                </AccordionSummary>
                <AccordionDetails>
                  <div className={classes.radioContainer}>
                    {option.children?.map((entry) => (
                      <div className={classes.radioItem}>
                        <Radio value={entry.value} active={value === entry.value} onClick={onChange} />
                        <label>{entry.label}</label>
                      </div>
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            ))}
            {/* TODO - Replace with proper message component */}
            <span className="scha__message error"> {error?.message}</span>
          </>
        )}
        name={"subject"}
        control={props.control}
        rules={{ required: { value: true, message: t("contactUsPage.form.fields.subject.placeholder")! } }}
        defaultValue={null}
      />
      <div className={"mt-3 spacer"} />
      <p className={classes.subjectHeader}>{t("serviceCases.create.comment") + "*"}</p>
      <Controller
        render={({ field: { ref, ...props }, fieldState: { error } }) => (
          <TextField
            variant={"outlined"}
            multiline
            fullWidth
            rows={4}
            className={classes.inputStyle}
            //label={t("request.information.comment.label")}
            helperText={t("request.information.comment.helper")}
            placeholder={t("serviceCases.create.placeholder")}
            error={!!error}
            inputRef={ref}
            {...props}
          />
        )}
        name={"comment"}
        control={props.control}
        rules={{ required: true, maxLength: 512 }}
        defaultValue={""}
      />
    </>
  );
};

export default RequestFeedbackPopupForm;
